import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class EeBriaSettingsCalculated extends ModelObject<EeBriaSettingsCalculated> {

    public passwordLength: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.passwordLength = jsonSerializedObject.passwordLength;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.passwordLength = this.passwordLength;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "passwordLength", rule: (v: any) => v == null || v >= -2147483648 || "PasswordLength is not greater than minimum allowed value: -2147483648"},
            {field: "passwordLength", rule: (v: any) => v == null || v <= 2147483647 || "PasswordLength is above maximum allowed value: 2147483647"},
    ];
}


export class EeBriaSettingsReadOnly extends ModelObject<EeBriaSettingsReadOnly> {

    /**
    * EeBria username.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public username: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.username = jsonSerializedObject.username;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.username = this.username;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class EeBriaSettings extends ModelObject<EeBriaSettings> {

    public version: number = 0;
    private _outletId: Guid | null = null;
    /**
    * Outlet Id for all EeBria order imports.
    */
    public get outletId(): Guid | null { return this._outletId }
    public set outletId(value: Guid | null) {
      this._outletId = value === undefined ? null : value;
    }
    private _stockLocationId: Guid | null = null;
    /**
    * StockLocation Id. If provided then products on EeBria will be updated with the Stock Location's stock level in BrewMan.
    */
    public get stockLocationId(): Guid | null { return this._stockLocationId }
    public set stockLocationId(value: Guid | null) {
      this._stockLocationId = value === undefined ? null : value;
    }
    /**
    * Whether the current integration should run continuously in the background.
    */
    public runInBackground: boolean = false;
    private _ignoreBeforeDate: LocalDate | null = null;
    /**
    * If provided then Orders on EeBria from before this date will be ignored.
    */
    public get ignoreBeforeDate(): LocalDate | null { return this._ignoreBeforeDate }
    public set ignoreBeforeDate(value: LocalDate | null) {
      this._ignoreBeforeDate = value === undefined ? null : value;
    }
    /**
    * Whether to use the Received Price (listed price minus Eebria fees)or the Listed Price when importing orders .
    */
    public useReceivedPrice: boolean = false;
    public calculated: EeBriaSettingsCalculated = new EeBriaSettingsCalculated();
    public readOnly: EeBriaSettingsReadOnly = new EeBriaSettingsReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.outletId = jsonSerializedObject.outletId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.outletId);
          this.stockLocationId = jsonSerializedObject.stockLocationId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.stockLocationId);
          this.runInBackground = jsonSerializedObject.runInBackground;
          this.ignoreBeforeDate = jsonSerializedObject.ignoreBeforeDate == null ? null : LocalDate.parse(jsonSerializedObject.ignoreBeforeDate);
          this.useReceivedPrice = jsonSerializedObject.useReceivedPrice;
          this.calculated = new EeBriaSettingsCalculated(jsonSerializedObject.calculated);
          this.readOnly = new EeBriaSettingsReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.outletId = this.outletId == null ? null : this.outletId.toString();
        toRet.stockLocationId = this.stockLocationId == null ? null : this.stockLocationId.toString();
        toRet.runInBackground = this.runInBackground;
        toRet.ignoreBeforeDate = this.ignoreBeforeDate == null ? null : this.ignoreBeforeDate.toString();
        toRet.useReceivedPrice = this.useReceivedPrice;
        toRet.calculated = this.calculated.toJsonSerializedObject();
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "outletId", rule: (v: any) => v == null || !v.isEmpty() || "EeBriaSettings.OutletId cannot be empty"},
            {field: "stockLocationId", rule: (v: any) => v == null || !v.isEmpty() || "EeBriaSettings.StockLocationId cannot be empty"},
            {field: "calculated", rule: (v: any) => !!v || "calculated is required"},
            {field: "calculated", rule: (v: any) => v.validate()},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class EeBriaProductMapping extends ModelObject<EeBriaProductMapping> {

    /**
    * The id of an EeBria product.
    * Not Nullable
    */
    public eeBriaProductId: string = "";
    /**
    * The Brewman invoiceable item id the EeBria product is mapped to.
    */
    public brewmanInvoiceableItemId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.eeBriaProductId = jsonSerializedObject.eeBriaProductId;
          this.brewmanInvoiceableItemId = Guid.parseOrEmpty(jsonSerializedObject.brewmanInvoiceableItemId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.eeBriaProductId = this.eeBriaProductId;
        toRet.brewmanInvoiceableItemId = this.brewmanInvoiceableItemId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "brewmanInvoiceableItemId", rule: (v: any) => !v.isEmpty() || "EeBriaProductMapping.BrewmanInvoiceableItemId cannot be empty"},
    ];
}


export class EeBriaProduct extends ModelObject<EeBriaProduct> {

    /**
    * The EeBria Product Id.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public eeBriaProductId: string = "";
    /**
    * The name of this product.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public name: string = "";
    /**
    * The name of this product's vessel.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public vesselName: string = "";
    /**
    * The volume this product's vessel.
    */
    public volume: number = 0;
    /**
    * The volume unit name of this product's vessel.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public volumeUnitName: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.eeBriaProductId = jsonSerializedObject.eeBriaProductId;
          this.name = jsonSerializedObject.name;
          this.vesselName = jsonSerializedObject.vesselName;
          this.volume = jsonSerializedObject.volume;
          this.volumeUnitName = jsonSerializedObject.volumeUnitName;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.eeBriaProductId = this.eeBriaProductId;
        toRet.name = this.name;
        toRet.vesselName = this.vesselName;
        toRet.volume = this.volume;
        toRet.volumeUnitName = this.volumeUnitName;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class EeBriaSettingsService extends ServiceBase {

  public async getEeBriaSettings(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<EeBriaSettings> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => new EeBriaSettings(response.eeBriaSettings);;
    return this.makeJsonRequest("eeBriaSettings/v1/getEeBriaSettings", req, false, extractor, opts);
  };

  public async authenticate(username: string, password: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    tenantId:api.tenantId.toString(),
    username:username,
    password:password,
    };
    const extractor = (response:any) => response.accessToken;;
    return this.makeJsonRequest("eeBriaSettings/v1/authenticate", req, true, extractor, opts);
  };

  public async disconnect(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("eeBriaSettings/v1/disconnect", req, true, extractor, opts);
  };

  public async saveEeBriaSettings(eeBriaSettings: EeBriaSettings, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    eeBriaSettings:eeBriaSettings.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("eeBriaSettings/v1/saveEeBriaSettings", req, true, extractor, opts);
  };

  public async getProductMappings(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(EeBriaProductMapping)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.eeBriaProductMappings.map((v: any) => new EeBriaProductMapping(v));;
    return this.makeJsonRequest("eeBriaSettings/v1/getProductMappings", req, false, extractor, opts);
  };

  public async assignProductMapping(eeBriaProductId: string, brewmanInvoiceableItemId: Guid | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    eeBriaProductId:eeBriaProductId,
    brewmanInvoiceableItemId:brewmanInvoiceableItemId == null ? null : brewmanInvoiceableItemId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("eeBriaSettings/v1/assignProductMapping", req, true, extractor, opts);
  };

  public async getAllEeBriaProducts(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(EeBriaProduct)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.eeBriaProducts.map((v: any) => new EeBriaProduct(v));;
    return this.makeJsonRequest("eeBriaSettings/v1/getAllEeBriaProducts", req, false, extractor, opts);
  };

  public async refreshEeBriaProducts(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(EeBriaProduct)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.eeBriaProducts.map((v: any) => new EeBriaProduct(v));;
    return this.makeJsonRequest("eeBriaSettings/v1/refreshEeBriaProducts", req, true, extractor, opts);
  };
}

export const eeBriaSettingsService = new EeBriaSettingsService();